import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { Box, useTheme } from "@mui/material";
import { MouseEvent, useCallback } from "react";
import { Layer, ResponsiveLine, Serie, SliceTooltipProps } from "@nivo/line";
import { SenecLineChartTooltip } from "src/components/monitoring/senec-line-chart/SenecLineChartTooltip";
import { CompleteTheme, DatumValue, Point } from "@nivo/core";
import { TimeFrame } from "src/components/monitoring/commonTypes";
import { SelectedTimeValues } from "src/components/monitoring/senec-line-chart/lineChartTypes";
import dayjs from "dayjs";

type MonitoringLineProps = {
    data: Serie[];
    layers: Layer[];
    locale: Intl.Locale;
    timeframe: TimeFrame;
    selectedLegendTypes: string[];
    maxYValue: number;
    colors: string[];
    showSelectedTime: boolean;
    showTooltip: boolean;
    customTooltipDateFormat?: Intl.DateTimeFormatOptions;
    onSelectedTimeValues?: (value: SelectedTimeValues) => void;
    onClick?: (point: Point, event: MouseEvent) => void;
    tooltipValueMapper?: (value: number | string, id: number | string) => string;
};

function getLineChartTheme(theme: SenecBaseTheme): Partial<CompleteTheme> {
    return {
        crosshair: {
            line: {
                stroke: "rgb(0,0,0)",
                strokeWidth: 2,
                strokeOpacity: 1,
                strokeDasharray: "2 2",
            },
        },
        tooltip: {
            container: { borderRadius: "12px", border: `1px solid ${theme.palette.neutralGray20.main}` },
        },
    } as Partial<CompleteTheme>;
}

export const MonitoringLine = ({
    data,
    layers,
    timeframe,
    selectedLegendTypes,
    maxYValue,
    colors,
    locale,
    showSelectedTime,
    showTooltip,
    customTooltipDateFormat,
    onSelectedTimeValues,
    onClick,
    tooltipValueMapper = undefined,
}: MonitoringLineProps) => {
    const theme: SenecBaseTheme = useTheme();

    const SenecLineChartTooltipWrapper = useCallback(
        (props: SliceTooltipProps) => (
            <SenecLineChartTooltip
                {...props}
                data={data}
                locale={locale}
                selectedLegendTypes={selectedLegendTypes}
                showTooltip={showTooltip}
                customTooltipDateFormat={customTooltipDateFormat}
                onSelectedTimeValues={onSelectedTimeValues}
                tooltipValueMapper={tooltipValueMapper}
            />
        ),
        [data, selectedLegendTypes, onSelectedTimeValues],
    );

    return (
        <Box style={{ position: "absolute", height: "346px", left: "16px", right: "16px", zIndex: 20 }}>
            <ResponsiveLine
                role={"monitor-charts"}
                data={data}
                theme={getLineChartTheme(theme)}
                margin={{ top: 9, right: 0, bottom: 1, left: 0 }}
                xScale={{
                    type: "time",
                    precision: "second",
                    min: dayjs(timeframe.start).toDate(),
                    max: dayjs(timeframe.end).toDate(),
                }}
                yScale={{
                    type: "linear",
                    min: 0,
                    max: maxYValue || 1,
                    reverse: false,
                }}
                useMesh={true}
                isInteractive={showSelectedTime}
                enableSlices={"x"}
                sliceTooltip={SenecLineChartTooltipWrapper}
                yFormat={(value: DatumValue) => Number(value).toFixed(2)}
                colors={colors}
                enableArea={true}
                areaBaselineValue={0}
                layers={layers}
                onClick={onClick}
            />
        </Box>
    );
};
